import { message, Spin } from 'antd';
import { useQuery } from '@apollo/client';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import Header from '~pages/onboarding/header';
import Footer from '~pages/onboarding/footer';
import PropertyType from '~pages/onboarding/property-type';
import ResidentType from '~pages/onboarding/resident-type';
import Address from '~pages/onboarding/address';
import Name from '~pages/onboarding/name';
import CoverPhoto from '~pages/onboarding/cover-photo';
import Business from '~pages/onboarding/business';
import BgImage from '~pages/onboarding/bg-image';
import OnboardingContext from './context';
import useOnboarding from './useOnboarding';
import ProcessSuccessful from './successful';
import RecalledToOnboarding from './recalled-to-onboarding';
import { onboardingProcesses } from './context';
import { QUERY_FIRST_PROPERTY } from './queries';
import { importModal } from '~helpers/import-modal';

const Onboarding = ({ landlordId, refetchQueryLandlordCompanyInfo }) => {
  const history = useHistory();

  const { loading, data } = useQuery(QUERY_FIRST_PROPERTY, {
    variables: {
      pageNumber: 1,
      pageSize: 1,
      isCheckAuth: false,
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const firstProperty = useMemo(
    () => data?.properties?.edges?.[0]?.node ?? {},
    [data?.properties?.edges],
  );

  const { onboardingData, handleClickBack, handleClickNext } = useOnboarding({
    landlordId,
    propertyInfo: {
      ...firstProperty,
      image: firstProperty?.heroImage ?? undefined,
      businessScale: firstProperty?.landlord?.businessScale,
      loading: loading,
    },
  });

  useEffect(() => {
    const queryInfo = queryString.parse(history?.location?.search);

    const isInVerifyEmailPage = !!queryInfo['verify-email-token'];
    if (isInVerifyEmailPage) {
      if (window.hurricane?.staticHash) {
        // 显示 verify email 弹框
        importModal();
      } else {
        document.addEventListener('staticHashInitialised', () => {
          importModal();
        });
      }
    }
    return () => {
      document.removeEventListener('staticHashInitialised', () => {
        importModal();
      });
    };
  }, [history]);

  return (
    <OnboardingContext.Provider value={onboardingData}>
      <Header />
      <BgImage />

      <div
        className={classnames('address-wrapper', {
          hidden: onboardingData.process !== onboardingProcesses.PROPERTY_ADDRESS,
        })}
      >
        <Address />
      </div>
      <Choose>
        <When condition={onboardingData.process === undefined || loading}>
          <Spin size="large" />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.PROPERTY_TYPE}>
          <PropertyType propertyLoading={loading} />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.RESIDENT_TYPE}>
          <ResidentType />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.PROPERTY_NAME}>
          <Name />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.COVER_PHOTO}>
          <CoverPhoto />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.BUSINESS_INFO}>
          <Business />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.SUCCESSFUL}>
          <ProcessSuccessful refetchQueryLandlordCompanyInfo={refetchQueryLandlordCompanyInfo} />
        </When>
        <When condition={onboardingData.process === onboardingProcesses.RECALLED_TO}>
          <RecalledToOnboarding
            landlordName={firstProperty.landlordName}
            processCount={!firstProperty?.heroImage ? 2 : 1}
            onNext={handleClickNext}
            propertyName={firstProperty?.name}
          />
        </When>
      </Choose>
      {onboardingData.process !== onboardingProcesses.SUCCESSFUL && (
        <Footer onBack={handleClickBack} onNext={handleClickNext} />
      )}
    </OnboardingContext.Provider>
  );
};

Onboarding.propTypes = {
  landlordId: PropTypes.string,
  propertyInfo: PropTypes.object,
  refetchQueryLandlordCompanyInfo: PropTypes.func.isRequired,
};

export default Onboarding;
